import { useState } from 'react';
import { useQuery } from 'react-query';

import { DashboardTemplate } from 'modules/v2/templates';
import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { getPrintCreditsHistory } from 'modules/api/digitalServices/mailOnDemand';
import { cn } from 'modules/v2/common/utils/cn';

import CreditsBalanceWidget from '../components/CreditsBalanceWidget';
import strings, { formatMessage } from '../strings';
import { CREDITS_HISTORY_PAGE_SIZE } from '../constants';
import { formatCredits, formatDate } from '../utils';

const formatDelta = (delta) => {
  const value = Number(delta || 0);

  return value > 0 ? `+${formatCredits(value)}` : formatCredits(value);
};

export const MailOnDemandCreditsContent = () => {
  const [page, setPage] = useState(1);

  const { data: history, isLoading, isError, refetch } = useQuery(
    ['modCreditsHistory', page],
    () => getPrintCreditsHistory(page, CREDITS_HISTORY_PAGE_SIZE),
    { keepPreviousData: true },
  );

  const meta = history?.meta || {};
  const rows = history?.data || [];

  const renderHistory = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-3 py-12">
          <Spinner />
          <span className="text-neutral-500">{strings.credits.historyLoading}</span>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex flex-col items-start gap-3 py-6">
          <p>{strings.credits.historyLoadError}</p>
          <Button color="outlined" onClick={() => refetch()}>
            {strings.common.retry}
          </Button>
        </div>
      );
    }

    if (rows.length === 0) {
      return <p className="py-8 text-center text-neutral-500">{strings.credits.historyEmpty}</p>;
    }

    return (
      <>
        <table className="w-full text-sm" data-testid="mod-credits-history">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs uppercase text-neutral-400">
              <th className="py-2">{strings.credits.columnDate}</th>
              <th className="py-2">{strings.credits.columnReason}</th>
              <th className="py-2 text-right">{strings.credits.columnDelta}</th>
              <th className="py-2 text-right">{strings.credits.columnBalanceAfter}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => (
              <tr key={entry.id} className="border-b border-neutral-100 last:border-0">
                <td className="py-2 text-neutral-500">{formatDate(entry.createdAt)}</td>
                <td className="py-2">{entry.reason}</td>
                <td
                  className={cn(
                    'py-2 text-right font-medium',
                    Number(entry.delta) > 0 ? 'text-green-600' : 'text-error-500',
                  )}
                >
                  {formatDelta(entry.delta)}
                </td>
                <td className="py-2 text-right">{formatCredits(entry.balanceAfter)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex items-center justify-between">
          <Button
            color="outlined"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            data-testid="mod-credits-prev"
          >
            {strings.credits.previousPage}
          </Button>
          <span className="text-sm text-neutral-500">
            {formatMessage(strings.credits.pageIndicator, {
              // meta.currentPage echoes the DS 0-indexed request page; the
              // local state is the 1-based page the member sees.
              currentPage: page,
              lastPage: meta.lastPage || 1,
            })}
          </span>
          <Button
            color="outlined"
            size="sm"
            disabled={page >= (meta.lastPage || 1)}
            onClick={() => setPage((current) => current + 1)}
            data-testid="mod-credits-next"
          >
            {strings.credits.nextPage}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <CreditsBalanceWidget showBuyButton />
      </div>

      <h3 className="mb-3 text-lg font-semibold">{strings.credits.historyTitle}</h3>
      {renderHistory()}
    </div>
  );
};

const MailOnDemandCreditsPage = () => (
  <DashboardTemplate hasSidebar title={strings.credits.pageTitle}>
    <MailOnDemandCreditsContent />
  </DashboardTemplate>
);

export default MailOnDemandCreditsPage;
